import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, StatusBar } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Colors } from '@/constants/colors';
import { Ionicons } from '@expo/vector-icons';
import { StockHeader } from './components/StockHeader';
import { IntelligentOutlookTab } from './components/IntelligentOutlookTab';
import { ChartTab } from './components/ChartTab';
import { polygonMarketDataService } from '@/services/api/polygonMarketData.service';

type TabType = 'outlook' | 'chart';

interface StockDetailScreenProps {
  route: {
    params: {
      ticker: string;
      companyName: string;
      groupId?: string;
      initialTab?: TabType;
    };
  };
  navigation: any;
}

export const StockDetailScreen: React.FC<StockDetailScreenProps> = ({ route, navigation }) => {
  const { ticker, companyName, groupId, initialTab = 'outlook' } = route.params;
  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [stockPrice, setStockPrice] = useState<number | undefined>(undefined);
  const [priceChange, setPriceChange] = useState<number | undefined>(undefined);
  const [priceChangePercent, setPriceChangePercent] = useState<number | undefined>(undefined);
  const [priceLoading, setPriceLoading] = useState(true);

  // Fetch live stock price
  useEffect(() => {
    const fetchPrice = async () => {
      try {
        setPriceLoading(true);
        console.log(`[StockDetailScreen] Fetching price for ${ticker}`);

        const { data, error } = await polygonMarketDataService.getQuote(ticker);

        if (error || !data) {
          console.error(`[StockDetailScreen] Failed to fetch price:`, error);
          setPriceLoading(false);
          return;
        }

        setStockPrice(data.price);
        setPriceChange(data.change);
        setPriceChangePercent(data.changePercent);
        console.log(
          `[StockDetailScreen] ✅ Price for ${ticker}: $${data.price} (${data.changePercent >= 0 ? '+' : ''}${data.changePercent.toFixed(2)}%)`
        );
      } catch (err) {
        console.error(`[StockDetailScreen] Error fetching price:`, err);
      } finally {
        setPriceLoading(false);
      }
    };

    fetchPrice();
  }, [ticker]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'outlook':
        return (
          <IntelligentOutlookTab ticker={ticker} companyName={companyName} groupId={groupId} />
        );
      case 'chart':
        return <ChartTab ticker={ticker} groupId={groupId} />;
      default:
        return null;
    }
  };

  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <StatusBar barStyle="light-content" />

      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <Ionicons name="chevron-back" size={24} color={Colors.text.primary} />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Stock Details</Text>
        <View style={styles.headerRight} />
      </View>

      {/* Stock Price Header */}
      <StockHeader
        ticker={ticker}
        companyName={companyName}
        price={stockPrice}
        change={priceChange}
        changePercent={priceChangePercent}
        isLoading={priceLoading}
      />

      {/* Tab Bar */}
      <View style={styles.tabBar}>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'outlook' && styles.activeTab]}
          onPress={() => setActiveTab('outlook')}
        >
          <Ionicons
            name="analytics"
            size={20}
            color={activeTab === 'outlook' ? Colors.primary : Colors.text.tertiary}
          />
          <Text style={[styles.tabText, activeTab === 'outlook' && styles.activeTabText]}>
            Outlook
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.tab, activeTab === 'chart' && styles.activeTab]}
          onPress={() => setActiveTab('chart')}
        >
          <Ionicons
            name="bar-chart"
            size={20}
            color={activeTab === 'chart' ? Colors.primary : Colors.text.tertiary}
          />
          <Text style={[styles.tabText, activeTab === 'chart' && styles.activeTabText]}>Chart</Text>
        </TouchableOpacity>
      </View>

      {/* Tab Content */}
      <View style={styles.tabContent}>{renderTabContent()}</View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  backButton: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  headerRight: {
    width: 40,
  },
  tabBar: {
    flexDirection: 'row',
    backgroundColor: Colors.background.primary,
    borderBottomWidth: 1,
    borderBottomColor: Colors.border.subtle,
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    gap: 6,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  activeTab: {
    borderBottomColor: Colors.primary,
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: Colors.text.tertiary,
  },
  activeTabText: {
    color: Colors.primary,
    fontWeight: '600',
  },
  tabContent: {
    flex: 1,
  },
});
